import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Moon, Sun, ArrowLeft, Clock, Info, Share2, BookmarkPlus, Bell } from "lucide-react";

const CYCLE_MIN = 90;                // one sleep cycle ≈ 90 minutes
const FALL_ASLEEP_MIN_DEFAULT = 15;

function pad(n) { return String(n).padStart(2, "0"); }
function fmt(date, use24) {
  let h = date.getHours(); const m = date.getMinutes();
  if (use24) return `${pad(h)}:${pad(m)}`;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${pad(m)} ${ampm}`;
}
function nextOccurrence(hh, mm, mustBeFuture = true) {
  const now = new Date();
  const d = new Date(now); d.setHours(hh, mm, 0, 0);
  if (mustBeFuture && d <= now) d.setDate(d.getDate() + 1);
  return d;
}
function addMinutes(date, min) { return new Date(date.getTime() + min * 60_000); }

export default function SleepCalculator() {
  const [mode, setMode] = useState("wake");           // "wake" (know wake time) | "bed" (know bed time)
  const [time, setTime] = useState("07:00");
  const [fallAsleep, setFallAsleep] = useState(true);
  const [use24, setUse24] = useState(false);
  const [saved, setSaved] = useState(() => JSON.parse(localStorage.getItem("jixxeo_sleep_schedule") || "null"));

  useEffect(() => { document.title = "Jixxeo Sleep Calculator"; }, []);

  const buffer = fallAsleep ? FALL_ASLEEP_MIN_DEFAULT : 0;
  const results = useMemo(() => calc(time, mode, buffer), [time, mode, buffer]);
  const tonight = useMemo(() => results.find((r) => r.recommended) || results[0], [results]);

  const saveSchedule = () => {
    const payload = { mode, time, fallAsleep, use24, saved_at: new Date().toISOString() };
    localStorage.setItem("jixxeo_sleep_schedule", JSON.stringify(payload));
    setSaved(payload);
    toast.success("Schedule saved");
  };

  const share = async () => {
    const lines = results.map((r) => `${r.emoji} ${fmt(r.dt, use24)} — ${r.cycles} cycles${r.recommended ? " ⭐" : ""}`).join("\n");
    const text = mode === "wake"
      ? `To wake up at ${fmt(nextOccurrence(...parseHM(time)), use24)}, go to bed at:\n${lines}\n\nJixxeo Sleep Calculator`
      : `Sleeping at ${fmt(nextOccurrence(...parseHM(time), false), use24)}, wake up at:\n${lines}\n\nJixxeo Sleep Calculator`;
    try { if (navigator.share) { await navigator.share({ title: "My sleep plan", text }); return; } } catch (e) { if (e?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Sharing not available"); }
  };

  const enableNotifs = async () => {
    if (!("Notification" in window)) return toast.error("Not supported on this device");
    const perm = await Notification.requestPermission();
    if (perm === "granted") toast.success("Reminders on — we'll ping you 15 mins before bed");
    else toast.error("Notifications denied");
  };

  return (
    <div data-testid="sleep-app" className="min-h-screen bg-gradient-to-b from-[#0A0620] via-[#1A0B3D] to-[#0A0620] text-[#F5F0FF]" style={{ fontFamily: "'Cabinet Grotesk','Outfit',ui-sans-serif" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0620]/85 border-b border-[#7A00FF]/25">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/browse" data-testid="sleep-back" className="p-2 -ml-2 rounded-full hover:bg-white/10"><ArrowLeft className="w-4 h-4" /></Link>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Moon className="w-5 h-5 text-[#B47BFF]" />
            <div>
              <div className="font-black text-lg leading-none">Jixxeo Sleep</div>
              <div className="text-[9px] uppercase tracking-widest text-white/40">Calculator</div>
            </div>
          </div>
          <button data-testid="sleep-24h" onClick={() => setUse24((v) => !v)}
            className="text-[10px] font-bold uppercase tracking-widest bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">
            {use24 ? "24h" : "12h"}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-28 space-y-5">
        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2 bg-white/5 border border-white/10 rounded-full p-1" data-testid="sleep-mode">
          <ModeBtn active={mode === "wake"} onClick={() => setMode("wake")} icon={<Sun className="w-4 h-4" />} label="Wake at…" testId="mode-wake" />
          <ModeBtn active={mode === "bed"} onClick={() => setMode("bed")} icon={<Moon className="w-4 h-4" />} label="Sleep at…" testId="mode-bed" />
        </div>

        {/* Big time picker */}
        <section className="rounded-3xl border border-[#7A00FF]/30 bg-black/30 backdrop-blur p-6 text-center">
          <div className="text-[10px] uppercase tracking-widest text-white/50">
            {mode === "wake" ? "What time do you want to wake up?" : "What time are you going to bed?"}
          </div>
          <input data-testid="sleep-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="mt-3 mx-auto block bg-transparent text-6xl sm:text-7xl font-black text-[#B47BFF] focus:outline-none tabular-nums w-full text-center"
            style={{ colorScheme: "dark" }} />
        </section>

        {/* Toggle */}
        <label className="flex items-center gap-3 text-sm bg-white/5 rounded-2xl border border-white/10 px-4 py-3" data-testid="sleep-fallasleep">
          <input type="checkbox" checked={fallAsleep} onChange={(e) => setFallAsleep(e.target.checked)}
            className="w-4 h-4 accent-[#B47BFF]" />
          <span className="flex-1">I need about <b>15 minutes</b> to fall asleep</span>
        </label>

        {/* Tonight's recommendation */}
        {tonight && (
          <section data-testid="sleep-tonight"
            className="rounded-3xl border-2 border-[#B47BFF]/50 bg-gradient-to-br from-[#B47BFF]/25 via-[#7A00FF]/10 to-transparent p-5"
            style={{ boxShadow: "0 0 26px rgba(180,123,255,0.25)" }}>
            <div className="text-[10px] uppercase tracking-widest text-[#B47BFF] font-bold flex items-center gap-1">
              <Info className="w-3 h-3" /> Tonight's recommendation
            </div>
            <div className="mt-1 font-black text-4xl leading-none text-[#B47BFF]">{fmt(tonight.dt, use24)}</div>
            <div className="text-sm text-white/70 mt-1">
              {mode === "wake" ? "Go to bed" : "Wake up"} at this time — you'll complete <b>{tonight.cycles}</b> full sleep cycles ({(tonight.cycles * 1.5).toFixed(1)}h).
            </div>
          </section>
        )}

        {/* All options */}
        <section data-testid="sleep-results">
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">
            {mode === "wake" ? "Bedtimes ranked by rest quality" : "Wake-up windows"}
          </div>
          <ul className="space-y-2">
            {results.map((r) => (
              <li key={r.cycles} data-testid={`sleep-slot-${r.cycles}`}
                className={"flex items-center gap-3 rounded-2xl border px-4 py-3 " + (r.recommended
                  ? "border-[#B47BFF]/50 bg-[#B47BFF]/12"
                  : "border-white/10 bg-white/5")}>
                <div className="text-2xl">{r.emoji}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-lg tabular-nums">{fmt(r.dt, use24)}</div>
                  <div className="text-[11px] text-white/60">{r.cycles} sleep cycles · {(r.cycles * 1.5).toFixed(1)}h {r.recommended && "· recommended"}</div>
                </div>
                {r.recommended && <span className="text-[#FFD700] text-lg">⭐</span>}
              </li>
            ))}
          </ul>
        </section>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button data-testid="sleep-save" onClick={saveSchedule}
            className="inline-flex items-center gap-1 bg-[#B47BFF] text-black font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
            <BookmarkPlus className="w-3.5 h-3.5" /> Save schedule
          </button>
          <button data-testid="sleep-share" onClick={share}
            className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
          <button data-testid="sleep-notify" onClick={enableNotifs}
            className="inline-flex items-center gap-1 border border-[#B47BFF]/30 bg-[#B47BFF]/10 text-[#B47BFF] font-black text-xs uppercase tracking-widest px-4 py-2.5 rounded-full">
            <Bell className="w-3.5 h-3.5" /> Bedtime reminder
          </button>
        </div>

        {saved && (
          <div className="text-[11px] text-white/40 text-center">Last saved schedule: {saved.mode === "wake" ? "wake" : "sleep"} at {saved.time} · {new Date(saved.saved_at).toLocaleDateString()}</div>
        )}

        {/* Explainer */}
        <details className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm" data-testid="sleep-explainer">
          <summary className="font-black text-[#B47BFF] cursor-pointer flex items-center gap-1"><Clock className="w-4 h-4" /> How this works</summary>
          <div className="mt-2 text-white/70 leading-relaxed space-y-2">
            <p>Sleep happens in cycles of roughly <b>90 minutes</b>. If you wake between cycles you feel refreshed; if you wake mid-cycle you feel groggy.</p>
            <p>We recommend <b>5 or 6 cycles</b> (7.5 or 9 hours) for most adults. Younger sleepers often need more.</p>
            <p><b>These are recommended sleep-cycle times, not guaranteed optimal bedtimes.</b> Sleep needs vary from person to person.</p>
          </div>
        </details>
      </main>
    </div>
  );
}

function ModeBtn({ active, onClick, icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={"flex items-center justify-center gap-1.5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-colors " +
        (active ? "bg-[#B47BFF] text-black" : "text-white/60 hover:text-white")}>
      {icon} {label}
    </button>
  );
}

function parseHM(t) { const [h, m] = t.split(":").map(Number); return [h || 0, m || 0]; }

/**
 * Compute the six candidate times (1 through 6 sleep cycles). "Recommended"
 * = 5 or 6 cycles (adults need 7.5–9h). Ordered best-first.
 */
function calc(time, mode, fallAsleepMin) {
  const [h, m] = parseHM(time);
  const anchor = nextOccurrence(h, m, mode === "wake");
  const rows = [];
  for (let c = 6; c >= 1; c--) {
    const totalMin = c * CYCLE_MIN + (mode === "wake" ? fallAsleepMin : 0);
    const dt = mode === "wake" ? addMinutes(anchor, -totalMin) : addMinutes(anchor, fallAsleepMin + c * CYCLE_MIN);
    const recommended = c === 5 || c === 6;
    const emoji = mode === "wake" ? (c >= 5 ? "🌙" : "😴") : (c >= 5 ? "☀️" : "🌤️");
    rows.push({ cycles: c, dt, recommended, emoji });
  }
  return rows;
}
